/**
 * useOttoDogKeyboard
 *
 * Keyboard control composable for the OttoDog 3D component.
 * Maps keyboard input to animation clip names.
 *
 * Arrow keys    → Walk in 4 directions
 * Ctrl+Arrows   → Run in 4 directions
 * W / Shift+W   → Swim forward / float
 * Space / J     → Jump (inplace / root-motion)
 * B             → Bark
 * A / Shift+A   → Attack combat / bite
 * Z / X / V     → Attack 3 / hand / fallback
 * S / Shift+S   → Sit / Sit up
 * L             → Side sit
 * E             → Eat
 * C             → Check
 * D / Shift+D   → Die / Die 2
 * R / Shift+R   → Reborn / Relive
 * H             → Hit
 * F             → Fall
 * I             → Idle
 */

import { ref, onBeforeUnmount } from 'vue';
import { heyAgent } from '../config/brand';

/**
 * A keymap label in both languages. The app's label tables carry their copy as
 * an `[en, sl]` pair (`tt(...label)` reads the right half at the call site), so
 * a binding's description is a pair too — the panel is on screen in Slovenian
 * as well as English. `display` stays a plain string: it is the keycap legend
 * of a physical key, not a word to translate.
 */
export type KeyLabel = [en: string, sl: string];

export interface KeyBinding {
  id: string; // normalized key id used for lookup
  display: string; // keycap legend shown in the keymap UI (a key, not a word)
  clipName: string; // exact animation clip name in the GLB model
  label: KeyLabel; // short description, in both languages
  group: 'walk' | 'run' | 'swim' | 'action';
}

export const KEY_BINDINGS: KeyBinding[] = [
  // ── Walk (Arrow Up/Down + A/D, hold) ──────────────────────────────────────
  { id: 'arrowup', display: '↑', clipName: 'Walk_Forward_Inplace', label: ['Walk Fwd', 'Hoja naprej'], group: 'walk' },
  {
    id: 'arrowdown',
    display: '↓',
    clipName: 'Walk_Backward_Inplace',
    label: ['Walk Back', 'Hoja nazaj'],
    group: 'walk',
  },
  { id: 'a', display: 'A', clipName: 'Walk_Left_RM', label: ['Walk Left', 'Hoja levo'], group: 'walk' },
  { id: 'd', display: 'D', clipName: 'Walk_Right_RM', label: ['Walk Right', 'Hoja desno'], group: 'walk' },
  // ── Run (Shift+Arrow/AD, hold) ─────────────────────────────────────────
  {
    id: 'shift+arrowup',
    display: 'Sh+↑',
    clipName: 'Run_Forward_Inplace',
    label: ['Run Fwd', 'Tek naprej'],
    group: 'run',
  },
  {
    id: 'shift+arrowdown',
    display: 'Sh+↓',
    clipName: 'Run_Backward_Inplace',
    label: ['Run Back', 'Tek nazaj'],
    group: 'run',
  },
  { id: 'shift+arrowleft', display: 'Sh+←', clipName: 'Run_Left_RM', label: ['Run Left', 'Tek levo'], group: 'run' },
  {
    id: 'shift+arrowright',
    display: 'Sh+→',
    clipName: 'Run_Right_RM',
    label: ['Run Right', 'Tek desno'],
    group: 'run',
  },
  { id: 'shift+a', display: 'Sh+A', clipName: 'Run_Left_RM', label: ['Run Left', 'Tek levo'], group: 'run' },
  { id: 'shift+d', display: 'Sh+D', clipName: 'Run_Right_RM', label: ['Run Right', 'Tek desno'], group: 'run' },
  // ── Swim (Alt+Arrow, hold) ────────────────────────────────────────
  {
    id: 'alt+arrowup',
    display: 'Alt+↑',
    clipName: 'Swim_Forward_2',
    label: ['Swim Fwd', 'Plavanje naprej'],
    group: 'swim',
  },
  {
    id: 'alt+arrowdown',
    display: 'Alt+↓',
    clipName: 'Swim_Backward',
    label: ['Swim Back', 'Plavanje nazaj'],
    group: 'swim',
  },
  {
    id: 'alt+arrowleft',
    display: 'Alt+←',
    clipName: 'Swim_Left_RM',
    label: ['Swim Left', 'Plavanje levo'],
    group: 'swim',
  },
  {
    id: 'alt+arrowright',
    display: 'Alt+→',
    clipName: 'Swim_Right_RM',
    label: ['Swim Right', 'Plavanje desno'],
    group: 'swim',
  },
  // ── Actions ───────────────────────────────────────────────────────
  // The 1 key greets the agent by name (`heyAgent`), which is the product's own
  // phrase — the same word in both languages, so both halves carry it.
  { id: '1', display: '1', clipName: '__hey_otto__', label: [heyAgent, heyAgent], group: 'action' },
  { id: '2', display: '2', clipName: 'Sitting', label: ['Sit', 'Sedi'], group: 'action' },
  { id: '3', display: '3', clipName: 'Falling', label: ['Sleep', 'Spanje'], group: 'action' },
  { id: '4', display: '4', clipName: 'Check', label: ['Check', 'Preveri'], group: 'action' },
  { id: '5', display: '5', clipName: 'Barking', label: ['Bark', 'Lajanje'], group: 'action' },
  { id: '6', display: '6', clipName: 'Attack_Bite', label: ['Bite', 'Ugriz'], group: 'action' },
  { id: '7', display: '7', clipName: 'Attack_Combat', label: ['Attack', 'Napad'], group: 'action' },
  { id: '8', display: '8', clipName: 'Eat', label: ['Eat', 'Jesti'], group: 'action' },
  { id: 'i', display: 'I', clipName: 'IdleA', label: ['Idle', 'Mirovanje'], group: 'action' },
  { id: 'space', display: 'Space', clipName: 'Jump_Inplace', label: ['Jump', 'Skok'], group: 'action' },
];

// Fast lookup map: normalized key id → binding
const KEY_MAP = new Map<string, KeyBinding>(KEY_BINDINGS.map((b) => [b.id, b]));

// WASD keys handled as orbit (not animation)
const ORBIT_KEYS = new Set(['w', 's', 'arrowleft', 'arrowright']);

function normalizeKey(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.ctrlKey) parts.push('ctrl');
  if (e.altKey) parts.push('alt');
  if (e.shiftKey) parts.push('shift');
  const k = e.key === ' ' ? 'space' : e.key.toLowerCase();
  parts.push(k);
  return parts.join('+');
}

export function useOttoDogKeyboard(
  onPlay: (clipName: string) => void,
  onIdle: () => void,
  onOrbit?: (key: string, pressed: boolean) => void,
) {
  const activeBinding = ref<KeyBinding | null>(null);
  const isFocused = ref(false);
  const heldKeys = new Set<string>();
  let _el: HTMLElement | null = null;

  function onKeyDown(e: KeyboardEvent) {
    // Don't fire when typing in inputs
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

    const rawKey = e.key === ' ' ? 'space' : e.key.toLowerCase();

    // When a modifier is pressed while movement keys are held, re-evaluate them
    if (['shift', 'control', 'alt'].includes(rawKey)) {
      const modPrefix = rawKey === 'control' ? 'ctrl' : rawKey;
      for (const held of [...heldKeys]) {
        const base = held.includes('+') ? held.split('+').pop()! : held;
        const newId = modPrefix + '+' + base;
        if (KEY_MAP.has(newId) && !heldKeys.has(newId)) {
          heldKeys.delete(held);
          heldKeys.add(newId);
          const binding = KEY_MAP.get(newId)!;
          activeBinding.value = binding;
          onPlay(binding.clipName);
        }
      }
      return;
    }

    // WASD → orbit (pass to scene, don't play animation)
    if (ORBIT_KEYS.has(rawKey) && !e.ctrlKey && !e.altKey && !e.shiftKey) {
      onOrbit?.(rawKey, true);
      return;
    }

    const keyId = normalizeKey(e);
    const binding = KEY_MAP.get(keyId);
    if (!binding) return;

    if (heldKeys.has(keyId)) return; // already playing, don't restart
    heldKeys.add(keyId);
    activeBinding.value = binding;
    onPlay(binding.clipName);
  }

  function onKeyUp(e: KeyboardEvent) {
    const rawKey = e.key === ' ' ? 'space' : e.key.toLowerCase();

    // WASD orbit release — only when no modifier (shift+arrowleft is an animation key)
    if (ORBIT_KEYS.has(rawKey) && !e.ctrlKey && !e.altKey && !e.shiftKey) {
      onOrbit?.(rawKey, false);
      // Also clean up any held combo for this raw key and resume if needed
      for (const k of [...heldKeys]) {
        if (k === rawKey || k.endsWith('+' + rawKey)) heldKeys.delete(k);
      }
      const stillHeld = [...heldKeys].find((k) => KEY_MAP.has(k));
      if (stillHeld) {
        const binding = KEY_MAP.get(stillHeld)!;
        activeBinding.value = binding;
        onPlay(binding.clipName);
      } else {
        activeBinding.value = null;
        onIdle();
      }
      return;
    }

    // Pure modifier release — switch held combos back to base key bindings
    if (['control', 'shift', 'alt', 'meta'].includes(rawKey)) {
      const prefix = rawKey === 'control' ? 'ctrl+' : rawKey + '+';
      for (const k of [...heldKeys]) {
        if (k.startsWith(prefix)) {
          const base = k.slice(prefix.length);
          heldKeys.delete(k);
          if (KEY_MAP.has(base)) {
            heldKeys.add(base);
            const binding = KEY_MAP.get(base)!;
            activeBinding.value = binding;
            onPlay(binding.clipName);
          }
        }
      }
      return;
    }

    // Remove this action key in any modifier combo from the held set
    for (const k of [...heldKeys]) {
      if (k === rawKey || k.endsWith('+' + rawKey)) heldKeys.delete(k);
    }

    // If other movement keys are still held, resume the last one; otherwise idle
    const stillHeld = [...heldKeys].find((k) => KEY_MAP.has(k));
    if (stillHeld) {
      const binding = KEY_MAP.get(stillHeld)!;
      activeBinding.value = binding;
      onPlay(binding.clipName);
    } else {
      activeBinding.value = null;
      onIdle();
    }
  }

  function onFocus() {
    isFocused.value = true;
  }

  function onBlur() {
    isFocused.value = false;
    heldKeys.clear();
    activeBinding.value = null;
    onIdle();
    // Release all orbit keys so the camera stops on blur
    for (const k of ORBIT_KEYS) onOrbit?.(k, false);
  }

  function activate(el?: HTMLElement | null) {
    _el = el ?? null;
    // Always listen globally so Otto responds regardless of focus
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    if (_el) {
      _el.addEventListener('focus', onFocus);
      _el.addEventListener('blur', onBlur);
    }
  }

  function deactivate() {
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
    if (_el) {
      _el.removeEventListener('focus', onFocus);
      _el.removeEventListener('blur', onBlur);
      _el = null;
    }
    heldKeys.clear();
    activeBinding.value = null;
    isFocused.value = false;
  }

  onBeforeUnmount(deactivate);

  return {
    activeBinding,
    isFocused,
    activate,
    deactivate,
  };
}
